/**
 * V113: MM 마스터 설정 페이지
 * V113.1: 토글 의미 재정의 - MM현황 표시 여부만 제어
 *
 * 조직별 MM 표시 여부(enabled) 설정
 * - 모든 orgUnit(회사/본부/실/팀) ON/OFF 토글 가능
 * - ON: MM현황 페이지에 해당 조직 데이터 표시
 * - OFF: MM현황 페이지에서 해당 조직 데이터 제외
 * - 접근 권한과는 무관, 상위/하위 전파 없음
 *
 * @route /mm/master
 * @access role_exec, admin
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Settings, Loader2, AlertCircle, Check, X, Building2, Users } from 'lucide-react';
import { useMmOrgSettings } from '../../shared/hooks/useMmOrgSettings';
import { useMmOrgSettingsMutation } from '../../shared/hooks/useMmOrgSettingsMutation';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { MmOrgSettingsItem } from '../../shared/types/mm';

/**
 * 조직 유형 라벨
 */
const ORG_TYPE_LABELS: Record<string, string> = {
  company: '회사',
  headquarters: '본부',
  division: '실',
  team: '팀',
};

/**
 * 조직 아이콘
 */
function OrgIcon({ type }: { type: string }) {
  if (type === 'team') {
    return <Users size={16} style={{ color: 'var(--color-primary-600)' }} />;
  }
  return <Building2 size={16} style={{ color: 'var(--color-gray-400)' }} />;
}

/**
 * 토글 스위치 컴포넌트
 * V113.3: ON=초록, OFF=빨강 색상 적용
 */
function ToggleSwitch({
  enabled,
  disabled,
  onChange,
  isLoading,
}: {
  enabled: boolean;
  disabled: boolean;
  onChange: () => void;
  isLoading: boolean;
}) {
  // V113.3: 상태별 색상 정의
  const trackColor = disabled
    ? 'var(--color-gray-200)'
    : enabled
    ? '#22c55e' // green-500 (ON)
    : '#ef4444'; // red-500 (OFF)

  const iconColor = enabled ? '#16a34a' : '#dc2626'; // green-600 / red-600

  return (
    <button
      onClick={onChange}
      disabled={disabled || isLoading}
      aria-label={enabled ? 'MM 표시 활성화됨' : 'MM 표시 비활성화됨'}
      aria-pressed={enabled}
      style={{
        position: 'relative',
        width: '48px',
        height: '26px',
        borderRadius: '13px',
        border: 'none',
        backgroundColor: trackColor,
        cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: enabled ? '25px' : '3px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : enabled ? (
          <Check size={12} style={{ color: iconColor }} />
        ) : (
          <X size={12} style={{ color: iconColor }} />
        )}
      </span>
    </button>
  );
}

/**
 * 조직 행 컴포넌트
 */
function OrgRow({
  org,
  level,
  onToggle,
  isToggling,
}: {
  org: MmOrgSettingsItem;
  level: number;
  onToggle: (orgUnitId: string, enabled: boolean) => void;
  isToggling: boolean;
}) {
  const handleToggle = useCallback(() => {
    onToggle(org.orgUnitId, !org.enabled);
  }, [org.orgUnitId, org.enabled, onToggle]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-gray-100)',
        backgroundColor: org.isTeam ? 'white' : 'var(--color-gray-50)',
      }}
    >
      {/* 들여쓰기 */}
      <div style={{ width: `${level * 24}px` }} />

      {/* 아이콘 + 이름 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: 1,
        }}
      >
        <OrgIcon type={org.type} />
        <span
          style={{
            fontWeight: org.isTeam ? 500 : 400,
            color: org.isTeam ? 'var(--color-gray-900)' : 'var(--color-gray-500)',
          }}
        >
          {org.name}
        </span>
        <span
          style={{
            fontSize: '12px',
            color: 'var(--color-gray-400)',
            backgroundColor: 'var(--color-gray-100)',
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {ORG_TYPE_LABELS[org.type] || org.type}
        </span>
      </div>

      {/* V113.1: 모든 조직 토글 가능 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ToggleSwitch
          enabled={org.enabled}
          disabled={false}
          onChange={handleToggle}
          isLoading={isToggling}
        />
      </div>
    </div>
  );
}

/**
 * 조직 트리 구성
 */
function buildOrgTree(
  orgUnits: MmOrgSettingsItem[]
): { org: MmOrgSettingsItem; level: number }[] {
  // parentId → children 맵 구성
  const childrenMap = new Map<string | null, MmOrgSettingsItem[]>();

  for (const org of orgUnits) {
    const parentId = org.parentId;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(org);
  }

  // DFS로 트리 순회
  const result: { org: MmOrgSettingsItem; level: number }[] = [];

  function traverse(parentId: string | null, level: number) {
    const children = childrenMap.get(parentId) || [];
    // 이름순 정렬
    children.sort((a, b) => a.name.localeCompare(b.name, 'ko'));

    for (const child of children) {
      result.push({ org: child, level });
      traverse(child.orgUnitId, level + 1);
    }
  }

  traverse(null, 0);
  return result;
}

/**
 * MM 마스터 페이지
 */
export function MMMasterPage() {
  // V314: 통합어드민 국가 선택
  const authContext = useAuthContext();
  const hasGlobalScopeAccess = useMemo(() => {
    const roleIds = authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
    return roleIds.includes('system_admin') || roleIds.includes('role_global');
  }, [authContext?.roleAssignments]);
  const [filterCompanyCode, setFilterCompanyCode] = useState<'KR' | 'CN'>('KR');

  const { orgUnits, isLoading, error, refetch } = useMmOrgSettings({
    // V314: 통합어드민은 선택한 국가 전달
    ...(hasGlobalScopeAccess && { companyCode: filterCompanyCode }),
  });
  const { setMmOrgEnabled, isSaving } = useMmOrgSettingsMutation();

  // 현재 토글 중인 orgUnitId
  const [togglingOrgId, setTogglingOrgId] = useState<string | null>(null);

  // 토글 핸들러
  const handleToggle = useCallback(
    async (orgUnitId: string, enabled: boolean) => {
      setTogglingOrgId(orgUnitId);
      try {
        await setMmOrgEnabled(orgUnitId, enabled);
        await refetch();
      } catch (err) {
        // 에러는 훅에서 처리됨
      } finally {
        setTogglingOrgId(null);
      }
    },
    [setMmOrgEnabled, refetch]
  );

  // 트리 구조로 변환
  const orgTree = buildOrgTree(orgUnits);

  // 통계 (V113.1: 모든 조직 대상)
  const totalCount = orgUnits.length;
  const enabledCount = orgUnits.filter((o) => o.enabled).length;

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: 'var(--color-gray-900)',
          }}
        >
          MM 마스터 설정
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* V314: 통합어드민 국가 선택 */}
          {hasGlobalScopeAccess && (
            <select
              value={filterCompanyCode}
              onChange={(e) => setFilterCompanyCode(e.target.value as 'KR' | 'CN')}
              style={{
                padding: '6px 12px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
              }}
            >
              <option value="KR">KR (한국)</option>
              <option value="CN">CN (대련)</option>
            </select>
          )}

        {/* 통계 */}
        {!isLoading && !error && (
          <div
            style={{
              display: 'flex',
              gap: '16px',
              fontSize: '14px',
              color: 'var(--color-gray-600)',
            }}
          >
            <span>
              전체 조직: <strong>{totalCount}</strong>개
            </span>
            <span>
              MM 표시: <strong style={{ color: 'var(--color-primary-600)' }}>{enabledCount}</strong>개
            </span>
          </div>
        )}
        </div>
      </div>

      {/* 안내 메시지 */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: 'var(--color-blue-50)',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: 'var(--color-blue-700)',
        }}
      >
        <Settings size={16} />
        <span>
          조직별 MM 표시 여부를 설정합니다. OFF된 조직은 MM현황 페이지에 표시되지 않습니다.
          (접근 권한과는 무관하며, 상위/하위 조직에 영향을 주지 않습니다)
        </span>
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
            color: 'var(--color-gray-500)',
          }}
        >
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '8px' }} />
          <span>조직 목록 로딩 중...</span>
        </div>
      )}

      {/* 에러 */}
      {error && (
        <div
          style={{
            padding: '16px',
            backgroundColor: 'var(--color-red-50)',
            border: '1px solid var(--color-red-200)',
            borderRadius: '8px',
            color: 'var(--color-red-700)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <AlertCircle size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>조회 실패</div>
            <div style={{ fontSize: '13px' }}>{error.message}</div>
          </div>
          <button
            onClick={() => refetch()}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: 'var(--color-red-100)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            다시 시도
          </button>
        </div>
      )}

      {/* 조직 목록 */}
      {!isLoading && !error && (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--color-gray-200)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {/* 헤더 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: 'var(--color-gray-100)',
              borderBottom: '1px solid var(--color-gray-200)',
              fontWeight: 600,
              fontSize: '13px',
              color: 'var(--color-gray-600)',
            }}
          >
            <div style={{ flex: 1 }}>조직</div>
            <div style={{ width: '100px', textAlign: 'center' }}>MM 사용</div>
          </div>

          {/* 목록 */}
          {orgTree.length === 0 ? (
            <div
              style={{
                padding: '48px',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
              }}
            >
              조직이 없습니다.
            </div>
          ) : (
            orgTree.map(({ org, level }) => (
              <OrgRow
                key={org.orgUnitId}
                org={org}
                level={level}
                onToggle={handleToggle}
                isToggling={isSaving && togglingOrgId === org.orgUnitId}
              />
            ))
          )}
        </div>
      )}

      {/* 저장 중 오버레이 */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            padding: '12px 20px',
            backgroundColor: 'var(--color-gray-800)',
            color: 'white',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Loader2 size={16} className="animate-spin" />
          <span>저장 중...</span>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
